import React from "react";
import { Link } from "react-router-dom";
import { useFreeOffered } from "../../../utils/freeOffered";

/**
 * NotARishtaBureau — positioning section.
 *
 * Purpose: stop visitors treating Select Proposal like a rishta aunty or a
 * rishta bureau ("rishta kara do"). We are not a matchmaker who introduces you
 * to the twenty families she happens to know and takes a cut of the shaadi.
 * We are a screening and search platform: we do the verification, the family
 * does the deciding.
 *
 * Layout: the two sides of the story are staged, not tabled.
 *   - full-bleed geometric watermark so the section never reads as empty white
 *   - LEFT margin (xl+): "the aunty's pile" — tilted handwritten biodata
 *     scraps, forwarded WhatsApp bubbles, an UNVERIFIED stamp
 *   - RIGHT margin (xl+): "the screened file" — clean verified profile cards,
 *     a locked-contact note, the 40% rejection dial
 *   - CENTER: two lanes, ours raised on white, instead of a flat grid
 * Margin decoration is decorative only (aria-hidden, pointer-events-none) and
 * hidden below xl, so mobile stays a clean two-lane stack.
 *
 * Rendered on both the standard and the Pakistan homepage. Keep the two
 * variants in this one component so the message can never drift apart.
 */

const ROWS = [
  {
    label: "First question",
    aunty: "Salary? Gora rung? Kitny kanal ka ghar?",
    sp: "Education. Profession. Character.",
  },
  {
    label: "Who you see",
    aunty: "The 20–30 she knows",
    sp: "Every verified member",
  },
  {
    label: "Verification",
    aunty: "“Achi family hai”",
    sp: "Checked by hand — ID, degree, job",
  },
  {
    label: "Your biodata",
    aunty: "Forwarded ×12",
    sp: "Contact hidden until you reveal",
  },
  {
    label: "Who decides",
    aunty: "She does",
    sp: "You do",
  },
  {
    label: "Cost",
    aunty: "Commission after the nikah",
    sp: "Fixed fee. No commission. Ever.",
  },
  {
    label: "Bad behaviour",
    aunty: "Awkward silence",
    sp: "Reported. Removed.",
  },
];

const VERIFIED = "#047857";

/* Handwriting-ish stack for the scraps — no webfont added, degrades gracefully */
const HAND = "'Segoe Script', 'Bradley Hand', 'Comic Sans MS', cursive";

const GeoWatermark = ({ accent }) => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none"
    style={{ opacity: 0.035 }}
    aria-hidden="true"
  >
    <defs>
      <pattern
        id="sp-geo-star"
        width="112"
        height="112"
        patternUnits="userSpaceOnUse"
        patternTransform="rotate(15)"
      >
        <path
          d="M43 4 L52 26 L74 17 L65 39 L82 43 L65 47 L74 69 L52 60 L43 82 L34 60 L12 69 L21 47 L4 43 L21 39 L12 17 L34 26 Z"
          fill="none"
          stroke={accent}
          strokeWidth="1.1"
        />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#sp-geo-star)" />
  </svg>
);

/* ── LEFT: the aunty's pile ─────────────────────────────────────────────── */
const AuntyPile = ({ accent }) => (
  <div
    className="hidden xl:block absolute left-0 top-28 w-[290px] h-full pointer-events-none select-none"
    aria-hidden="true"
  >
    <div
      className="absolute left-11 top-2 w-[200px] bg-white rounded-[3px] p-4 rotate-[-7deg] border border-[#ece6e2]"
      style={{ boxShadow: "0 8px 24px rgba(0,0,0,.08)" }}
    >
      <p className="text-[19px] leading-none mb-3 text-[#7a7a7a]" style={{ fontFamily: HAND }}>
        Biodata — Rukhsana ki cousin
      </p>
      <div className="space-y-2">
        <div className="h-2 bg-[#eee] rounded w-11/12" />
        <div className="h-2 bg-[#eee] rounded w-8/12" />
        <div className="h-2 bg-[#eee] rounded w-10/12" />
        <div className="h-2 bg-[#eee] rounded w-6/12" />
      </div>
      <div
        className="inline-block mt-4 px-2.5 py-1 text-[9px] font-bold uppercase rotate-[-6deg] rounded-[2px]"
        style={{
          border: `2.5px solid ${accent}73`,
          color: `${accent}8c`,
          letterSpacing: "0.18em",
        }}
      >
        unverified
      </div>
      <div
        className="absolute -right-5 -bottom-6 w-14 h-14 rounded-full"
        style={{ border: "3px solid rgba(150,110,70,.18)" }}
      />
    </div>

    <div
      className="absolute left-8 top-[250px] w-[180px] bg-white rounded-[3px] p-3.5 rotate-[5deg] border border-[#ece6e2]"
      style={{ boxShadow: "0 8px 24px rgba(0,0,0,.08)" }}
    >
      <p className="text-[18px] leading-tight text-[#8a8a8a]" style={{ fontFamily: HAND }}>
        Doctor larka — Canada?
        <br />
        photo nahi hai
      </p>
      <div className="mt-2.5 space-y-2">
        <div className="h-2 bg-[#eee] rounded w-10/12" />
        <div className="h-2 bg-[#eee] rounded w-7/12" />
      </div>
    </div>

    <div className="absolute left-[178px] top-[196px] max-w-[104px] bg-[#f4f4f2] rounded-2xl rounded-bl-sm px-3.5 py-2 rotate-[3deg] border border-[#e6e6e2]">
      <p className="text-[11.5px] text-[#6b6b6b] leading-snug">
        “achi family hai” <span className="text-[10px] text-[#a8a8a8]">↷ forwarded</span>
      </p>
    </div>
    <div className="absolute left-[168px] top-[400px] max-w-[112px] bg-[#f4f4f2] rounded-2xl rounded-bl-sm px-3.5 py-2 rotate-[-4deg] border border-[#e6e6e2]">
      <p className="text-[11.5px] text-[#6b6b6b] leading-snug">
        biodata group me daal diya{" "}
        <span className="text-[10px] text-[#a8a8a8]">↷ forwarded ×12</span>
      </p>
    </div>

    <p className="absolute left-2 top-[470px] text-[10px] font-bold tracking-[0.2em] uppercase text-[#b9b2ad] rotate-[-90deg] origin-left">
      the aunty’s pile
    </p>
  </div>
);

/* ── RIGHT: the screened file ───────────────────────────────────────────── */
const ScreenedFile = ({ accent }) => (
  <div
    className="hidden xl:block absolute right-0 top-24 w-[290px] h-full pointer-events-none select-none"
    aria-hidden="true"
  >
    <div
      className="absolute right-[236px] top-6 bottom-24 w-px"
      style={{ borderLeft: `1.5px dashed ${VERIFIED}47` }}
    />

    <div
      className="absolute right-7 top-0 w-[224px] bg-white rounded-xl p-4 border border-[#e8eeea]"
      style={{ boxShadow: "0 8px 24px rgba(0,0,0,.08)" }}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-bold text-white bg-[#1a0505]">
          MA
        </div>
        <div>
          <p className="text-[13px] font-bold text-[#222] leading-none">M. Ayesha, 27</p>
          <p className="text-[11px] text-[#777] mt-1">MBBS · Lahore</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {["✓ ID checked", "✓ Degree seen"].map((t) => (
          <span
            key={t}
            className="text-[9.5px] font-semibold px-2 py-1 rounded-full"
            style={{ backgroundColor: `${VERIFIED}12`, color: VERIFIED }}
          >
            {t}
          </span>
        ))}
      </div>
      <div
        className="mt-3 pt-3 flex items-center gap-2 text-[10.5px] text-[#888]"
        style={{ borderTop: "1px dashed #e8eeea" }}
      >
        <span>🔒</span> Contact hidden until you reveal
      </div>
    </div>

    <div
      className="absolute right-14 top-[190px] w-[210px] bg-white rounded-xl p-3.5 border border-[#e8eeea]"
      style={{ boxShadow: "0 8px 24px rgba(0,0,0,.08)" }}
    >
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white bg-[#3d2b2b]">
          SR
        </div>
        <div>
          <p className="text-[12px] font-bold text-[#222] leading-none">S. Rehman, 31</p>
          <p className="text-[10.5px] text-[#777] mt-1">CA · Karachi</p>
        </div>
        <span className="ml-auto text-[13px]" style={{ color: VERIFIED }}>
          ✓
        </span>
      </div>
    </div>

    {/* rejection dial — same ~40% figure published elsewhere on the site */}
    <div
      className="absolute right-10 top-[300px] w-[176px] bg-white rounded-xl px-4 py-4 text-center border border-[#e8eeea]"
      style={{ boxShadow: "0 8px 24px rgba(0,0,0,.08)" }}
    >
      <div className="relative w-[76px] h-[76px] mx-auto">
        <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f0eceb" strokeWidth="3" />
          <circle
            cx="18"
            cy="18"
            r="15.9"
            fill="none"
            stroke={accent}
            strokeWidth="3"
            strokeDasharray="40 100"
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-playfair text-[19px] font-bold text-[#222]">40%</span>
        </div>
      </div>
      <p className="text-[10.5px] text-[#666] mt-2 leading-snug">
        of applicants never make it
        <br />
        past our review
      </p>
    </div>

    <div className="absolute right-16 top-[470px] max-w-[190px]">
      <p className="text-[19px] leading-snug" style={{ fontFamily: HAND, color: VERIFIED }}>
        reviewed by a human.
        <br />
        every single one.
      </p>
    </div>
    <p className="absolute right-2 top-[430px] text-[10px] font-bold tracking-[0.2em] uppercase text-[#a9bdb4] rotate-90 origin-right">
      screened file
    </p>
  </div>
);

const NotARishtaBureau = ({ variant = "en", accent = "#a30000" }) => {
  const isUr = variant === "ur";
  const freeOffered = useFreeOffered();

  return (
    <section className="relative overflow-hidden py-16 md:py-24" style={{ backgroundColor: "#FDFBF9" }}>
      <GeoWatermark accent={accent} />

      {/* soft light pools so the margins are never flat white */}
      <div
        className="absolute -left-32 top-24 w-[420px] h-[420px] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${accent}12, transparent 65%)` }}
        aria-hidden="true"
      />
      <div
        className="absolute -right-32 bottom-10 w-[460px] h-[460px] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${VERIFIED}17, transparent 65%)` }}
        aria-hidden="true"
      />

      <AuntyPile accent={accent} />
      <ScreenedFile accent={accent} />

      <div className="relative max-w-[1000px] mx-auto px-5">
        {/* Headline */}
        <div className="text-center mb-11">
          <span
            className="inline-block text-[11px] font-bold tracking-[0.18em] uppercase px-4 py-1.5 rounded-full mb-4"
            style={{ backgroundColor: `${accent}12`, color: accent }}
          >
            What we are — and what we are not
          </span>

          {isUr && (
            <p
              className="font-urdu text-2xl md:text-3xl font-semibold mb-3"
              style={{ color: accent }}
              dir="rtl"
            >
              ہم رشتہ کرانے والے نہیں — ہم رشتہ پرکھنے والے ہیں
            </p>
          )}

          <h2 className="font-playfair text-3xl md:text-[2.7rem] font-bold text-[#222] leading-tight">
            We are not a rishta aunty. We are the screening
            <br className="hidden md:block" /> your family would do — at scale.
          </h2>

          <p className="text-[#555] mt-4 max-w-2xl mx-auto leading-relaxed">
            We don’t find you a rishta. We verify people — you decide.
          </p>
        </div>

        {/* Two lanes: theirs flat, ours raised */}
        <div className="grid md:grid-cols-2 gap-5 md:gap-7">
          <div className="rounded-2xl px-6 py-6 bg-[#f5f2f0] border border-[#e9e3df]">
            <p className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#9a9490] mb-5">
              Rishta aunty / bureau
            </p>
            <ul className="space-y-4">
              {ROWS.map((row) => (
                <li key={row.label}>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[#b3aca8]">
                    {row.label}
                  </p>
                  <p className="text-[14.5px] text-[#6e6864] mt-0.5">{row.aunty}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative rounded-2xl px-6 py-6 bg-white border border-[#ece6e2] shadow-[0_18px_50px_rgba(26,5,5,0.10)] md:-mt-3 md:mb-3">
            <span className="absolute -top-3 left-6 text-[10px] font-bold tracking-[0.16em] uppercase text-white px-3 py-1 rounded-full bg-[#1a0505]">
              Select Proposal
            </span>
            <p
              className="text-[11px] font-bold tracking-[0.16em] uppercase mb-5"
              style={{ color: accent }}
            >
              What we actually do
            </p>
            <ul className="space-y-4">
              {ROWS.map((row) => (
                <li key={row.label} className="flex gap-2.5">
                  <span style={{ color: VERIFIED }}>✓</span>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#aaa]">
                      {row.label}
                    </p>
                    <p className="text-[14.5px] font-medium text-[#222] mt-0.5">{row.sp}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Closing line + CTA */}
        <div className="mt-10 text-center">
          <p className="text-[#555] max-w-2xl mx-auto leading-relaxed mb-6">
            If your first question is salary, gora rung, or kitny kanal ka ghar — we are
            honestly not for you. Log in, filter, shortlist, reveal.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="inline-block px-8 py-3 rounded-full font-semibold text-white transition-all duration-300 hover:shadow-lg"
              style={{ backgroundColor: accent }}
            >
              {freeOffered ? "Create your verified profile — free" : "Create your verified profile"}
            </Link>
            <Link
              to="/how-we-are-different"
              className="inline-block px-8 py-3 rounded-full font-medium border-2 border-gray-300 text-[#222] hover:border-gray-500 transition-all duration-300"
            >
              See how verification works
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NotARishtaBureau;
